import { Button } from "@shared-ui/button";
import { Input } from "@shared-ui/input";
import { Label } from "@shared-ui/label";
import { Textarea } from "@shared-ui/textarea";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Dialog } from "@/components/ui/dialog";
import {
	type AdminCategoryRow,
	type CreateCategoryInput,
	createAdminCategory,
	type UpdateCategoryInput,
	updateAdminCategory,
} from "@/features/catalog/services/catalog-api.service";
import { apiErrorMessage } from "@/lib/api-error";

/**
 * Create or rename one taxonomy node.
 *
 * A dialog rather than a page, because a category is four fields and its
 * meaning is its POSITION -- which only the tree behind this shows. Sending an
 * operator to a separate screen to rename a node would take away the one piece
 * of context that tells them they are renaming the right one.
 *
 * It does not offer re-parenting. The endpoint accepts a `parentId` on update
 * and guards it against cycles, but moving a subtree changes the depth of
 * everything beneath it and can strand a spec template on a node that stops
 * being a leaf. That deserves its own screen and its own confirmation, not a
 * fifth field here.
 */

/**
 * A slug the server's `/^[a-z0-9-]+$/` will accept, or "" if the name carries
 * nothing usable.
 *
 * Exported because it is the one piece of this file with rules worth testing
 * directly: an Indian catalogue's category names carry ampersands, slashes and
 * parentheses ("Tiles & Stone", "Adhesives/Grouts"), and every one of them is
 * rejected by that regex if it survives into the slug.
 */
const TITLE_ID = "category-dialog-title";

export function slugify(name: string): string {
	return name
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");
}

export type CategoryDialogMode =
	| { kind: "create"; parent: AdminCategoryRow | null }
	| { kind: "edit"; category: AdminCategoryRow };

interface CategoryDialogProps {
	mode: CategoryDialogMode;
	onClose: () => void;
	onSaved: () => void;
}

export function CategoryDialog({
	mode,
	onClose,
	onSaved,
}: CategoryDialogProps) {
	const editing = mode.kind === "edit" ? mode.category : null;

	const [name, setName] = useState(editing?.name ?? "");
	const [slug, setSlug] = useState(editing?.slug ?? "");
	const [description, setDescription] = useState(editing?.description ?? "");
	const [displayOrder, setDisplayOrder] = useState(
		String(editing?.displayOrder ?? 0),
	);
	const [isActive, setIsActive] = useState(editing?.isActive ?? true);

	/*
	 * The slug follows the name until someone edits it, and then stops.
	 *
	 * Deriving it on every keystroke regardless would silently rewrite a slug an
	 * operator had deliberately shortened -- and a category slug is a URL, so it
	 * is not a display detail they can be surprised about later.
	 */
	const [slugEdited, setSlugEdited] = useState(mode.kind === "edit");

	function onNameChange(value: string): void {
		setName(value);
		if (!slugEdited) setSlug(slugify(value));
	}

	const save = useMutation({
		mutationFn: async () => {
			const order = Number.parseInt(displayOrder, 10);
			if (mode.kind === "create") {
				const input: CreateCategoryInput = {
					name: name.trim(),
					slug: slug.trim(),
					...(description.trim() ? { description: description.trim() } : {}),
					...(mode.parent ? { parentId: mode.parent.id } : {}),
					...(Number.isFinite(order) ? { displayOrder: order } : {}),
				};
				await createAdminCategory(input);
				return;
			}

			/*
			 * Only what MOVED, not the whole form.
			 *
			 * The same rule the brand editor follows: a PATCH carrying every field
			 * writes values the operator never looked at, and the audit trail then
			 * records a rename as a change to five columns.
			 */
			const patch: UpdateCategoryInput = {};
			if (name.trim() !== mode.category.name) patch.name = name.trim();
			if (slug.trim() !== mode.category.slug) patch.slug = slug.trim();
			if (description.trim() !== (mode.category.description ?? "")) {
				patch.description = description.trim();
			}
			if (Number.isFinite(order) && order !== mode.category.displayOrder) {
				patch.displayOrder = order;
			}
			if (isActive !== mode.category.isActive) patch.isActive = isActive;

			if (Object.keys(patch).length === 0) return;
			await updateAdminCategory(mode.category.id, patch);
		},
		onSuccess: () => {
			onSaved();
			onClose();
		},
	});

	const title =
		mode.kind === "edit"
			? `Edit ${mode.category.name}`
			: mode.parent
				? `New category under ${mode.parent.name}`
				: "New top-level category";

	const canSave = name.trim() !== "" && slug.trim() !== "" && !save.isPending;

	return (
		// The shared shell now, which is where Escape, the focus trap and the
		// focus restore live. This file used to say the app had no dialog
		// primitive and that two hand-rolled ones behaving alike beat one of each
		// -- true when written, and the reason four dialogs could be tabbed out of
		// into the page behind them.
		<Dialog
			open
			titleId={TITLE_ID}
			// Cancel is `disabled={save.isPending}`; Escape follows it.
			onClose={() => {
				if (!save.isPending) onClose();
			}}
			className="grid w-full max-w-lg gap-4 rounded-3xl border bg-white p-5 shadow-xl"
		>
			<div>
				<h2 id={TITLE_ID} className="text-lg font-semibold">
					{title}
				</h2>
				{mode.kind === "create" && mode.parent ? (
					<p className="text-sm text-muted-foreground">
						It will sit inside {mode.parent.name}.
					</p>
				) : null}
			</div>

			<div className="grid gap-1.5">
				<Label htmlFor="category-name">Name</Label>
				<Input
					id="category-name"
					value={name}
					onChange={(event) => onNameChange(event.target.value)}
				/>
			</div>

			<div className="grid gap-1.5">
				<Label htmlFor="category-slug">Slug</Label>
				<Input
					id="category-slug"
					value={slug}
					onChange={(event) => {
						setSlugEdited(true);
						setSlug(event.target.value);
					}}
				/>
				<p className="text-xs text-muted-foreground">
					Lowercase letters, numbers and hyphens. This is the category's URL.
				</p>
			</div>

			<div className="grid gap-1.5">
				<Label htmlFor="category-description">Description</Label>
				<Textarea
					id="category-description"
					rows={2}
					value={description}
					onChange={(event) => setDescription(event.target.value)}
				/>
			</div>

			<div className="grid gap-1.5">
				<Label htmlFor="category-order">Display order</Label>
				<Input
					id="category-order"
					type="number"
					min={0}
					value={displayOrder}
					onChange={(event) => setDisplayOrder(event.target.value)}
				/>
			</div>

			{mode.kind === "edit" ? (
				<label className="flex items-center gap-2 text-sm">
					<input
						type="checkbox"
						checked={isActive}
						onChange={(event) => setIsActive(event.target.checked)}
					/>
					Active
					<span className="text-xs text-muted-foreground">
						Inactive categories stay in the taxonomy and disappear from customer
						browsing.
					</span>
				</label>
			) : null}

			{save.isError ? (
				/*
				 * THE SERVER'S OWN SENTENCE, which is the point of rendering this at
				 * all rather than a generic failure.
				 *
				 * Adding a child to a category that carries a spec template is
				 * refused, and the refusal names the template -- because templates
				 * live only on leaves, so the child would strand it. The tree
				 * disables the control that leads here, but that judgement is made
				 * from a list that can be a minute old; this is what an operator
				 * sees when someone else added the template in between.
				 */
				<p role="alert" className="text-sm text-red-700">
					{apiErrorMessage(save.error, "Could not save this category.")}
				</p>
			) : null}

			<div className="flex justify-end gap-2 border-t pt-3">
				<Button variant="ghost" onClick={onClose} disabled={save.isPending}>
					Cancel
				</Button>
				<Button onClick={() => save.mutate()} disabled={!canSave}>
					{save.isPending ? "Saving..." : "Save"}
				</Button>
			</div>
		</Dialog>
	);
}
